<template>
  <div />
</template>

<script>

import Realisation from '@/models/Realisation'

import dot from 'dot-object'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { isObject } from '@/services/validation' // , isNumeric
// import { convertLetterToNumber } from '@/helpers/string'

import Vue from 'vue'

export default {
  name: 'BaseRealisationStep',
  data() {
    return {
      uuid: null,
      disabled: false,

      /**
       * A flag that is briefly set when cancelling a change of an already completed step
       *  It's used to reload the components by briefly changing their visibility
       */
      reloading: false,

      currentStep: 1,

      /**
       * Whether the form is active.
       *  It's not when the step has already been completed, and the user didn't explicitly activate the form.
       */
      locked: true,

      /**
       * Whether the step details are being corrected (meaning the progress should remain unaffected upon saving data)
       */
      correction: false,

      /**
       * Whether we're saving before going to the next step
       */
      goingToNextStep: false,
      redirectToAfterSubmit: null,

      /**
       *
       */
      feedback: null,
      feedbackMessage: '',

      /**
       * Docs = docs belonging to active Location, by doctype
       * RealistionDocs = docs belonging to active Realisation process, by doctype
       */
      Docs: {},
      RealisationDocs: {},
    }
  },
  computed: {
    ...mapGetters('requests', [
      'requestByUuid',
    ]),
    ...mapGetters('realisation', [
      'hasRecord',
      'recordByUuid',
    ]),
    ...mapGetters('relations', [
      'changeCount',
      'requestRelationChanges',
      'requestUuidsByRealisationUuid',
    ]),
    ...mapGetters('user', [
      'hasRole',
      'canAccessRequests',
    ]),
    ...mapGetters('tenant', [
      'getWorkflowUuidByName',
      'isCurrentTenant',
      'getEnabledContractors',
      'getWorkflowSpecByName',
      'getWorkflowSpecByUuid',
    ]),


    getRecordWorkflowSpecification() {
      return this.getWorkflowSpecByUuid({
        uuid: this.record.getWorkflowSpecificationUuid(),
      })
    },
    getWorkflowSteps() {
      return this.getRecordWorkflowSpecification?.steps || []
    },
    componentStepUuid() {
      let stepNumber = `${this.currentStep}`
      return this.getWorkflowSteps
        ?.find(step => step.short === stepNumber)
        ?.uuid
    },
    stepSpecificationsByUuid() {
      return this.getRecordWorkflowSpecification?.stepsByUuid || {}
    },
    getComponentStepSpec() {
      return this.stepSpecificationsByUuid[this.componentStepUuid]
    },
    routeUuid() {
      return this.$route.params.uuid
    },
    isRecordRoute() {
      return ! ['actief', 'cpo', 'gemeente', 'afgerond', 'geannuleerd', 'onhold'].includes(this.routeUuid.toLowerCase())
    },

    /**
     * Create a new record if the requested record cannot be found
     */
    record() {
      let record = this.recordByUuid({
        uuid: this.uuid || this.$route.params.uuid || null,
      }) || null


      if (record !== null) {
        return record
      }

      // TODO: Check whether we can move this to config?
      switch (process.env.VUE_APP_TENANT) {
        case 'park-n-charge':
          record = new Realisation({
            ref: null, data: {
              uuid: this.uuid,
              Locations: [{
                ChargePointOperator: 'Park&Charge',
              }],
            },
          })
          break
        default:
          // TODO: CBAPI - HARDCODED
          if (process.env.VUE_APP_CBAPI === 'true') {
            record = new Realisation({
              cbapi: true,
              ref: null,
              data: {
                uuid: this.uuid,
                status: {

                  step: null,
                  uuid: null,
                  version: null,
                  label: null,

                  completed: {
                    enabled: false,
                  },
                  cancelled: {
                    enabled: false,
                  },
                  onhold: {
                    enabled: false,
                  },
                  vkb: {
                    enabled: false,
                  },
                  gdpr: {
                    enabled: false,
                  },
                },
                flow: {
                  state: [{
                    'uuid': 'e2d71172-61ef-4245-a30a-0f588b67f030',
                    'started_at': Date.now(),
                  }],
                  uuid: '53c35fb3-c0f5-439c-8924-f0e4d7bf9e47',
                },
                MunicipalityCode: null,
                processType: this.processType || null,
              },
            })
          } else {
            record = new Realisation({
              cbapi: false,
              ref: null,
              data: {
                uuid: this.uuid,
              },
            })
          }
      }


      return record
    },

    /**
     * Whether or not the Realisation Process has been saved
     * TODO: CBAPI - RP - This used to be a check on ref
     */
    isUnsavedRecord() {
      return this.record.isUnsavedRecord()
    },

    hasProcessType() {
      return (this.record.processType || null) !== null
    },

    /**
     * The active step of the realisation proces
     */
    step() {
      if (! this.record) return '1'

      return this.record.getCurrentStepShort() || '1'
    },

    /**
     * Get the active & current step data from the configuration
     */
    currentStepData() {
      this.$store.dispatch('realisation/updateCurrentStep', this.currentStep)
      return this.getStepDataByNumber({ number: this.currentStep })
    },

    /**
     * The location details
     */
    values() {
      try {
        return this.record.values.Location
      } catch (e) {
        return {
          Location: {},
        }
      }
    },
    CurrentLocation() {
      return this.values
    },

    /**
     * Location version
     */
    version() {
      try {
        return this.values.version
      } catch (e) {
        return 1
      }
    },

    /**
     * The related requests UUIDS
     */
    requestUuids() {
      return this.requestUuidsByRealisationUuid({ uuid: this.record.uuid })
    },

    /**
     * The related request objects
     */
    connectedRequests() {
      return this.requestUuids.slice().map(uuid => {
        return this.requestByUuid({ uuid })
      })
    },

    /**
     * Whether the step can still be changed
     */
    openForChange() {
      return this.record.isCompleted() === false
        && this.record.isCancelled() !== true
        && this.record.isOnHold() !== true
    },

    /**
     * Whether the CPO Advice should be / was skipped
     *  This is due to the current location being suggested by the CPO
     */
    Skip() {
      return !!this.values.CPOAdvice.Skip
    },
    completed() {
      return !!this.record.isCompleted()
    },
    statusLocked() {
      return !!this.record.isLocked()
    },
    onhold() {
      return !!this.record.isOnHold()
    },
    cancelled() {
      return !!this.record.isCancelled()
    },
    cancelledOrOnHold() {
      return this.onhold || this.cancelled
    },
    disableNextBtn() {
      return this.cancelledOrOnHold && ! this.completedUuids.includes(
        this.currentStepData?.uuid,
      )
    },

    /**
     * Access
     */
    mayEdit() {
      return !this.statusLocked && this.currentStepData?.access.edit.some(role => this.hasRole({ role }))
    },

    /**
     * Record Flow State
     */

    recordState() {
      try {
        return this.record.flow.state
      } catch (e) {
        return []
      }
    },
    completedUuids() {
      return this.recordState
        .filter(state => state.completed_at)
        .map(state => state.uuid)
    },
    availableUuids() {
      return this.recordState
        .filter(state => state.started_at)
        .map(state => state.uuid)
    },
    isCompletedStep() {
      return this.completedUuids.includes(
        this.currentStepData?.uuid,
      )
    },
    isAvailableStep() {
      return this.availableUuids.includes(
        this.currentStepData?.uuid,
      )
    },
  },
  watch: {
    routeUuid(newValue) {
      if (newValue !== null) {
        this.uuid = newValue

        this.setCurrentlyOpenedRecord({ uuid: this.uuid })
      }
      this.clearChanges()
    },
    hasRecord() {
      if (! this.hasRecord) {
        this.redirectToStart()
      }
    },
    /**
     * When the record changes we check whether the form should be active
     */
    record() {

      this.locked = ! this.mayEdit
        || this.isCompletedStep
        || this.completed
        || this.onhold
        || this.cancelled

      if (this.hasRecord) {
        this.RealisationDocs = this.record.Docs
      }
    },
    changeCount: {
      immediate: true,
      async handler() {
        if (this.changeCount !== 0) {
          await this.storeRelationChanges()
        }
      },
    },
  },
  beforeCreate() {
    // Redirect filtered lists
    let uuid = this.$route.params.uuid
    this.uuid = uuid
    if (['actief', 'cpo', 'gemeente', 'afgerond', 'geannuleerd', 'onhold'].includes(uuid.toLowerCase())) {
      this.$router.replace({
        name: 'RealisationListFiltered',
        params: {
          filter: uuid,
        },
      })

      return false
    }
  },
  created() {
    this.uuid = this.routeUuid

    if (! this.hasRecord) {
      // console.log("no record")
      this.redirectToStart()
    } else if (this.isBeyondCurrentStep()) {
      // console.log("Don't think so!", this.step, this.currentStep)
      this.redirectToStep({ step: this.step })
      this.setCurrentlyOpenedRecord({ uuid: this.uuid })
    } else {
      this.setLastOpenedRecord({ uuid: this.uuid })
      this.setCurrentlyOpenedRecord({ uuid: this.uuid })
    }
  },
  beforeDestroy() {
    this.clearChanges()

    // Note: the route information is a reference to the new route, not the previous one that is cleaned up
    // We don't want to clean up the opened record reference if the new route is also a realisation step
    if (this.$route.name !== 'Realisation' && ! this.$route.name.startsWith('Realisation.Step')) {
      this.setCurrentlyOpenedRecord({ uuid: null })
    }
  },
  methods: {
    ...mapMutations('realisation', [
      'addRecord',
      'replaceRecord',
      'setLastOpenedRecord',
      'setCurrentlyOpenedRecord',
    ]),
    ...mapActions('realisation', [
      'loadRecordByUuid',
    ]),
    ...mapMutations('relations', [
      'setConnectionChange',
      'finalizeChanges',
      'clearChanges',
    ]),
    ...mapMutations('chargingpoints', [
      'replaceLocation',
    ]),
    ...mapMutations('requests', [
      'replaceRequest',
    ]),

    getStepDataByNumber({ number }) {
      return this.getWorkflowSteps
        .find(step => (step.step || step.short) == number) || null
    },

    redirectToStart() {
      if (this.currentStep === 1) return // Already there...
      const record = this.recordByUuid({ uuid: this.uuid })
      if (record) {
        this.$router.push({
          name: 'Realisation',
          params: {
            uuid: this.uuid,
          },
        })
      }
    },
    handleNext() {
      // console.log("Handle Next")

      let stepState = this.record
        .nextAvailableFlowStep({
          currentUuid: this.componentStepUuid,
        })

      // console.log(stepState, this.stepsByUuid[stepState.uuid].short )

      this.redirectToStep({
        step: this.stepSpecificationsByUuid[stepState.uuid].short,
      })


      // TODO: currentStep is no longer a number...
      // this.redirectToStep({ step: this.currentStep + 1 })
    },
    handlePrevious() {
      // TODO: currentStep is no longer a number...
      let stepState = this.record
        .previousAvailableFlowStep({
          currentUuid: this.componentStepUuid,
        })
      this.redirectToStep({
        step: this.stepSpecificationsByUuid[stepState.uuid].short,
      })
      // this.redirectToStep({ step: this.currentStep - 1 })
    },
    redirectToStep({ step }) {
      const record = this.recordByUuid({ uuid: this.uuid })
      if (record) {
        this.$router.push({
          name: `Realisation.Step${step}`,
          param: {
            uuid: this.uuid,
          },
        })
      }
    },

    /**
     * Submit and go to the next step
     */
    async handleSubmitAndNext () {
      // console.log("SUBMIT")

      this.$refs.form.validate()
      if (! this.$refs.form.isValid()) {
        this.handleValidationError()
        return
      }

      this.goingToNextStep = true
      let success = await this.handleSubmit()
      this.goingToNextStep = false

      if (success) {
        if (this.redirectToAfterSubmit) {
          this.redirectToStep({ step: this.redirectToAfterSubmit })
        } else {
          this.handleNext()
        }
      }
    },

    // Stub
    handleComplete() {},

    /**
     * Handle Validation Error
     */
    handleValidationError() {
      this.feedback = 'danger'
      this.feedbackMessage = 'Controleer of alle velden (juist) zijn ingevuld.'
    },


    handleFileChanges({ doctype, files }) {
      // this.Docs[doctype] = files
      Vue.set(this.Docs, doctype, files)
    },

    /**
     * Stub
     */
    prepIntention({ step, correction, complete, target }) {
      return { step, correction, complete, target }
    },

    /**
     * Stub
     */
    prepData({ data }) {
      return data
    },

    /**
     * Stub
     */
    isValidData() {
      return true
    },

    prepStatus() { // { data }
      // TODO: Maybe remove this method, now that this status has been replaced with Intention?
      return {}
    },

    postSave() {},


    async handleCreate() {
      try {
        this.disabled = true

        // Get & tranform form input
        const form = this.$refs['form']
        const values = form.getValues()
        let data = dot.object(values)

        let response = await this.$cbapi.process.create({
          geographyCode: this.record.getGeographyCode(),
          stakeholders: [
            data.Municipality,
            data.Contractor,
            data.GridOperator,
            data.ChargePointOperator,
          ].filter(slug => slug !== null),
          tenant: this.record.tenant,
          type: 'realisation',
          subtype: this.record.getProcessType() || this.processType,
        })

        // Clear any request relations
        this.clearChanges()

        // Mark as active record
        this.setCurrentlyOpenedRecord({ uuid: response?.data?.uuid })

        // Insert Record.
        await this.loadRecordByUuid({
          uuid: response?.data?.uuid,
        })

        this.$router.push({
          name: 'Realisation.Step1',
          params: {
            uuid: response?.data?.uuid,
          },
          // Pass along the query params
          query: this.$route.query,
        })

      } catch (e) {

        console.log(e, e.message)

        this.disabled = false
        this.feedback = 'danger'
        this.feedbackMessage = this.feedbackMessage || 'De informatie kon niet verwerkt worden door de server.'
        return false
      }
    },

    async handleEdit() {
      try {
        this.disabled = true
        this.feedback = null
        this.feedbackMessage = null

        let intent = this.prepIntention({
          step: this.currentStepData?.uuid,
          correction: this.correction,
          complete: this.goingToNextStep,
          target: null, // TODO: implement?
        })

        // Get & tranform form input
        const form = this.$refs['form']
        const values = form.getValues()
        let data = dot.object(values)

        data = this.prepData({ data })
        //console.log('Prepped data', data)

        if ( ! this.isValidData({ data })) {
          // console.log("invalid data")
          // Note: The validation method should set the feedback
          this.disabled = false
          return false
        }

        let response = await this.$cbapi.process.saveStep({
          uuid: this.record.getUuid(),
          intent,
          synced: this.record.getSyncedTS(),
          data,
        })

        if ((! response) || response === false) {
          console.log('saving step failed')
          throw new Error()
        }

        // // Get fresh copy of the record
        // response = await this.$cbapi.process.getByUuid({
        //   uuid: this.record.getUuid()
        // })

        // if (! response) {
        //   this.feedbackMessage = 'Het bijgewerkte record kon niet opgehaald worden. Ververs a.u.b. de pagina.'
        //   throw new Error()
        // }

        this.replaceRecord({
          record: response?.data,
        })

        this.postSave()
        this.disabled = false

        // Edit in place, without impacting the flow
        let correction = this.correction && ! this.goingToNextStep

        // Edit of completed step, and resetting the flow
        let reset = this.correction && this.goingToNextStep

        let message = correction ? 'De correctie is opgeslagen' : (reset ? 'De wijziging is opgeslagen' : 'De informatie is opgeslagen')

        this.$notify({
          success: true,
          title: 'Geslaagd',
          message,
        })

        return true

      } catch (e) {

        console.log(e, e.message)

        this.disabled = false
        this.feedback = 'danger'
        this.feedbackMessage = this.feedbackMessage || 'De informatie kon niet verwerkt worden door de server.'
        return false
      }
    },

    async handleSubmit() {
      if (this.isUnsavedRecord) {
        return await this.handleCreate()
      } else {
        return await this.handleEdit()
      }
    },

    /**
     * Handle Submit event
     */
    // async handleSubmitOld () {
    //   try {

    //     let intention = this.prepIntention({
    //       step: this.currentStepData?.uuid,
    //       correction: this.correction,
    //       complete: this.goingToNextStep,
    //       target: null, // TODO: implement?
    //     })

    //     this.feedback = null
    //     this.feedbackMessage = ''
    //     this.disabled = true

    //     // Get & tranform form input
    //     const form = this.$refs['form']
    //     const values = form.getValues()
    //     let data = dot.object(values)

    //     if (this.currentStep === '6' && this.correction) {
    //       const plannedDateCancelled = data.PlannedRealisation.PlannedDate === 'Cancelled'
    //       data.PlannedRealisation.PlannedDateCancelledAt = plannedDateCancelled
    //         ? new Date(Date.now()).toLocaleDateString('fr-CA') //YYYY-MM-DD
    //         : null

    //       data.PlannedRealisation.LastSavedPlannedDate = plannedDateCancelled
    //         ? this.values.PlannedRealisation.PlannedDate
    //         : null
    //     }

    //     data = this.prepData({ data })

    //     // Version
    //     data.version = this.version

    //     data.requestUuids = this.requestUuids

    //     // Get the record reference, or add the uuid in case of a new record
    //     const ref = this.record.ref || false
    //     if (ref === false) {
    //       // TODO: Check whether flow is booting up. Add context to Intention ?
    //       // if (this.currentStep === 1) {
    //       data.uuid = this.uuid
    //       // } else {
    //       //   throw new Error("Het interne realisatieproces referentie nummer ontbreekt")
    //       // }
    //     }

    //     data.step = this.componentStepUuid


    //     data.status = this.prepStatus({ data })
    //     data.correction = this.correction

    //     if ( ! this.isValidData({ ref, data })) {
    //       // console.log("invalid data")
    //       // Note: The validation method should set the feedback
    //       this.disabled = false
    //       return false
    //     }

    //     // set the proper endpoint
    //     let endpoint = '/api/realisation_step'
    //     if (! ref) {
    //       endpoint = '/api/realisation_create'

    //       // And add the flow uuid if we're creating a record
    //       // TODO: Note that in the future, the precise flow has to be known before
    //       // TODO:  the form can be loaded. That is why the UI provides the uuid.
    //       // TODO: Maybe provide an init endpoint that returns a shell record?
    //       // TODO: That is, with the flow state evaluated, but no record saved to FaunaDB.
    //       // TODO:  --- UPDATE: flow spec is loaded through the tenant config. Use that.
    //       data.flowUuid = this.getWorkflowUuidByName({
    //         name: 'realisation',
    //       })
    //     }

    //     /**
    //      * This is to 'upgrade' existing realisation processes
    //      */
    //     if (! this.record.getFlowUuid()) {
    //       data.flowUuid = this.getWorkflowUuidByName({
    //         name: 'realisation',
    //       })
    //     }

    //     // Make the call
    //     // TODO: CBAPI - RP - switch to rp-collection-post, rp-uuid--location-post & rp-uuid-post
    //     const token = await this.$auth.getTokenSilently()
    //     const api = await fetch(endpoint, {
    //       method: 'POST',
    //       headers: {
    //         Authorization: `Bearer ${token}`,
    //       },
    //       body: JSON.stringify({
    //         ref,
    //         data,
    //         intention,
    //       }),
    //     })
    //     if (! api.ok) {
    //       // console.log(api.status)
    //       if (api.status === 401) {
    //         this.feedbackMessage = 'U heeft niet de rechten om deze handeling uit te voeren. Mogelijk is uw sessie verlopen.'
    //       }
    //       if (api.status === 400) {
    //         this.feedbackMessage = 'De invoer is ongeldig verklaard. Neem contact op indien dit zich blijft voordoen.'
    //       }
    //       throw new Error(api.error)
    //     }
    //     const response = await api.json()

    //     this.finalizeChanges()
    //     this.clearChanges()

    //     if (ref) {
    //       this.replaceRecord({
    //         record: response.data.record,
    //       })
    //     } else {
    //       this.addRecord({
    //         record: response.data.record,
    //       })

    //       if (Array.isArray(this.requestUuids) && this.requestUuids.length !== 0) {
    //         await this.storeRelationChanges()
    //       }
    //     }

    //     if (Array.isArray(response.data.locations)) {
    //       response.data.locations.forEach(location => {
    //         this.replaceLocation({ location })
    //       })
    //     }

    //     this.postSave()

    //     this.disabled = false
    //     return true

    //   } catch (e) {
    //     console.log(e, e.message)

    //     this.disabled = false
    //     this.feedback = 'danger'
    //     this.feedbackMessage = this.feedbackMessage || 'De informatie kon niet verwerkt worden door de server.'
    //     return false
    //   }
    // },
    /**
     * Add / Remove Requests to Realisation
     */
    async addRequestsToRealisation() {

      if (this.record && this.requestAdditions) {
        this.requestAdditions.forEach(uuid => {
          this.setConnectionChange({
            type: 'add',
            requestUuid: uuid,
            realisationUuid: this.record.getUuid(),
          })
        })

        // await this.storeRelationChanges()
      }
    },
    handleBreakConnection({ uuid }) {
      this.setConnectionChange({
        type: 'remove',
        requestUuid: uuid,
        realisationUuid: this.record.uuid,
      })
    },

    storeRelationChanges: async function() {
      try {

        // Don't try to make a call that is going to fail
        // TODO: Show an error? Or not, because the call is intended to be ignored (e.g. RVB)?
        if (! this.canAccessRequests) return

        // Nothing to do...
        if (this.requestRelationChanges.length === 0) return

        this.feedback = null
        this.feedbackMessage = ''

        // Only continue if the record is available in the DB
        if (! this.record || this.record.isUnsavedRecord()) return

        /**
         * Go through changes one by one.
         *  There is almost always only 1 change, so let's not complicate matters for no purpose
         */
        for (let i = 0; i < this.requestRelationChanges.length; i++) {

          // { type: 'add|remove', requestUuid: '<uuid>', realisationUuid: '<uuid>' }
          let change = this.requestRelationChanges[0]

          let record = this.recordByUuid({
            uuid: change.realisationUuid,
          })

          if (! record || record.isUnsavedRecord()) {
            console.log('RP Record is not yet saved')
            continue
          }

          let request = this.requestByUuid({
            uuid: change.requestUuid,
          })

          let response = await this.$cbapi.process.setRequestConnection({
            uuid: change.realisationUuid,
            requestUuid: change.requestUuid,
            synced: record.getSyncedTS(),
            requestSynced: request.getSyncedTS(),
            action: change.type === 'add' ? 'connect' : 'disconnect',
          })

          // Get fresh copy of the record
          response = await this.$cbapi.process.getByUuid({
            uuid: change.realisationUuid,
          })
          this.replaceRecord({
            record: response?.data,
          })

          response = await this.$cbapi.requests.getByUuid({
            uuid: change.requestUuid,
          })
          this.replaceRequest({
            request: response,
          })
        }

        // let data = {
        //   step: 'relations',
        //   requestUuids: this.requestUuids,
        // }

        // // Make the call
        // // TODO: CBAPI - RP - switch to rp-uuid--request-post
        // const token = await this.$auth.getTokenSilently()
        // const api = await fetch('/api/realisation_relations', {
        //   method: 'POST',
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //   },
        //   body: JSON.stringify({
        //     ref,
        //     data,
        //   }),
        // })
        // if (!api.ok) {
        //   // console.log(api.status)
        //   if (api.status === 401) {
        //     this.feedbackMessage = 'U heeft niet de rechten om deze handeling uit te voeren. Mogelijk is uw sessie verlopen.'
        //   }
        //   if (api.status === 400) {
        //     this.feedbackMessage = 'De invoer is ongeldig verklaard. Neem contact op indien dit zich blijft voordoen.'
        //   }
        //   // console.log(api)
        //   throw new Error('Unable to store connection')
        // }
        // const response = await api.json()

        this.finalizeChanges()
        this.clearChanges()

        // this.replaceRecord({
        //   record: response.data.record,
        // })

      } catch (e) {
        console.log(e)

        this.feedback = 'danger'
        this.feedbackMessage = this.feedbackMessage || 'De aanvraag kon niet verbonden worden. Probeer het nog een keer.'
        this.clearChanges()
      }
    },
    handleStartChange() {
      this.locked = false
    },
    handleCancelChange() {
      this.reloading = true

      this.$nextTick(() => {
        this.reloading = false
        this.locked = true
        this.correction = false
      })
    },
    handleStartCorrection() {
      this.handleStartChange()
      this.correction = true
    },

    /**
     * Docs
     */
    minIdByDocType({ doctype }) {

      let docs = this.RealisationDocs[doctype] || null
      if (! Array.isArray(docs)) return 0

      return docs.reduce(( max, doc ) => {
        if (isObject(doc) && doc.id) {
          return Math.max(max, doc.id)
        } else {
          return Math.max(max, doc)
        }
      }, 0)
    },
    prepDocData({ doctype }) {
      let docs = this.Docs[doctype] || []

      return docs.filter(file => file.progress === 100).map(file => {
        return {
          id: file.id,
          ext: file.ext,
        }
      })
    },

    // TODO: This is a simplification !
    // TODO: Limit: parent steps / sub-steps order
    // TODO: Should use Record State...
    isBeyondCurrentStep() {

      // console.log("isBeyond", this.currentStepData, this.availableUuids)


      let uuid = this.currentStepData?.uuid

      return ! this.availableUuids.includes(uuid)


      // // Ensure our base input is a string
      // let step = this.step + ''
      // let currentStep = this.currentStep + ''

      // // Split in segments, divided by -
      // let stepSegments = step.split('-').map(segment => {
      //   if (isNumeric(segment)) {
      //     return parseInt(segment, 10)
      //   }
      //   return convertLetterToNumber(segment)
      // })

      // let currentSegments = currentStep.split('-').map(segment => {
      //   if (isNumeric(segment)) {
      //     return parseInt(segment, 10)
      //   }
      //   return convertLetterToNumber(segment)
      // })

      // if (stepSegments[0] === currentSegments[0]) {
      //   return stepSegments[1] < currentSegments[1]
      // }

      // return stepSegments[0] < currentSegments[0]

      // return this.step < this.currentStep
    },

    /**
     * triggered by comment 'sync' event
     */
    async handleSync() {
      //console.log('Sync please...')

      const response = await this.$cbapi.process.getByUuid({
        uuid: this.uuid,
      })
      this.replaceRecord({
        record: response?.data,
      })
    },
  },
}
</script>

<style lang="scss">
.RealisationDetail {
  text-align: left;
  padding: 18px 0 30px;

  &__map {
    position: relative;
    max-width: 100%;
    height: 100%;
  }

  &__aside {
    position: sticky !important;
    top: 0;
  }
  &__RequestList {
    list-style: none;
    margin: 0;
    padding: 0;

    .b-icon {
      cursor: pointer;

      &:hover {
        color: #ec0000;
      }
    }
  }
}
</style>
